import { Button, ICON_COLOR } from '@zep-us/zus';
import { Trans, useTranslation } from 'next-i18next';
import { BoltFillIcon, BoltSmallRectangleColorMintIcon, BoltSmallRectangleColorVioletIcon } from '@/icons';
import { calculateDaysUntilExpiry } from '@/module/plan/utils/calculateDaysUntilExpiry';
import { type PlanHeaderCtaLocation, useFreePlanCta, usePremiumAccess } from '@/shared/plan';
import { type PlanType, type UserInfo } from '@/types';
import { cn } from '@/utils/tailwind.util';

/** 버튼 겉모양만 바꾸는 옵션. 동작·문구는 그대로다. */
type PlanButtonAppearance = {
  /** 버튼 겉모양만 덮는다(배경 위 보드 헤더 등). */
  className?: string;
  size?: 'xs' | '2xs';
  /**
   * 주면 강조 요소(번개 아이콘·남은 일수)의 포인트색을 빼고 단색으로 칠한다.
   * 아이콘 path 의 fill 을 덮는 클래스(예: `[&_path]:fill-white`)를 넘긴다. 남은 일수는 버튼 글자색을 따른다.
   */
  accentClassName?: string;
};

/**
 * `location` 은 CTA 가 놓인 헤더 — FREE CTA 의 quiz_plan_header_cta_click 과 유료 버튼의
 * 플랜 안내 진입 경로(from)에 쓴다.
 */
export const HeaderPlanStatusButton = ({
  user,
  location,
  ...appearance
}: {
  user: UserInfo;
  location: PlanHeaderCtaLocation;
} & PlanButtonAppearance) => {
  if (user.planType === 'FREE') {
    return <FreePlanHeaderButton location={location} {...appearance} />;
  }
  return <PaidPlanHeaderButton user={user} location={location} {...appearance} data-sentry-element="PaidPlanHeaderButton" data-sentry-component="HeaderPlanStatusButton" data-sentry-source-file="HeaderPlanStatusButton.tsx" />;
};

/**
 * FREE 사용자의 헤더 CTA (Figma 28913:53540).
 *
 * 기존에는 「FREE 플랜 이용 중」이라고 상태만 알렸다. 시안은 **지금 할 수 있는 일**을
 * 말하게 바꿨고, 같은 FREE 라도 네 가지 상태로 갈린다.
 *
 * | 상태 | 라벨 | 동작 |
 * |---|---|---|
 * | 핸무체 이용권 보유 | 2주 PRO 체험 시작하기 | 체험 시작 다이얼로그 |
 * | 일반 이용권 보유 | 이용권 사용하기 | 사용 가능한 이용권 안내 다이얼로그 |
 * | 휴대폰 미인증(핸무체 대상) | PRO 플랜 2주 무료 체험하기 | /verification-phone |
 * | 해외 이무체 대상 | PRO 플랜 2주 무료 체험하기 | 즉시 발급 + 보상 모달 |
 * | 그 외 | 플랜 업그레이드 | /pricing 새 탭 |
 */
const FreePlanHeaderButton = ({
  location,
  ...appearance
}: {
  location: PlanHeaderCtaLocation;
} & PlanButtonAppearance) => {
  const {
    t
  } = useTranslation(['common']);
  const {
    state: headerState,
    isPending,
    execute
  } = useFreePlanCta({
    surface: 'product-header',
    location
  });
  if (headerState.type === 'loading') {
    return <div aria-hidden className={cn('w-40 rounded-full skeleton-shimmer', appearance.size === '2xs' ? 'h-[26px]' : 'h-[30px]')} />;
  }
  const label = (() => {
    switch (headerState.type) {
      case 'trial-voucher':
        return t('common:plan.freeTrial.start');
      case 'usable-voucher':
        return t('common:voucher.action.activate');
      // 이무체(해외)도 같은 문구다 — 아직 이용권이 없고 "체험을 받는다"는 제안이라
      // 시안이 두 상태에 같은 라벨(13810:3829)을 쓴다. 키 이름만 인증 시절 것이다.
      case 'phone-trial':
      case 'two-weeks-trial':
        return t('common:plan.freeTrial.phoneVerification');
      case 'upgrade':
        return t('common:planUpgrade');
    }
  })();
  return <PlanCtaChip label={label} disabled={isPending} onClick={() => void execute()} {...appearance} data-sentry-element="PlanCtaChip" data-sentry-component="FreePlanHeaderButton" data-sentry-source-file="HeaderPlanStatusButton.tsx" />;
};

/**
 * 시안 공통 칩 — `Button/transparent/neutral` 에 violet 테두리와 그림자를 얹은 형태.
 * ZUS Button 에는 이 조합의 variant 가 없어 className 으로 보강한다.
 */
const PlanCtaChip = ({
  label,
  disabled,
  className,
  size = 'xs',
  accentClassName,
  onClick
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
} & PlanButtonAppearance) => <Button variant="transparent" intent="neutral" size={size} rounded="full" className={cn('cursor-pointer border border-violet-400 bg-white shadow-sm', className)} leftSlot={<BoltSmallRectangleColorVioletIcon size="xs" className={accentClassName} />} disabled={disabled} onClick={onClick} data-sentry-element="Button" data-sentry-component="PlanCtaChip" data-sentry-source-file="HeaderPlanStatusButton.tsx">
    {label}
  </Button>;
const PaidPlanHeaderButton = ({
  user,
  location,
  className,
  size = 'xs',
  accentClassName
}: {
  user: UserInfo;
  location: PlanHeaderCtaLocation;
} & PlanButtonAppearance) => {
  const {
    openPlanGuide
  } = usePremiumAccess();
  const handleClick = () => {
    if (user.planType === 'PRO') {
      window.open('/pricing', '_blank', 'noopener,noreferrer');
      return;
    }

    // from 은 체험 trial_source 로도 흐른다 — 새 location 은 trialTracking.ts TRIAL_SOURCES 에도 추가할 것.
    void openPlanGuide({
      highlightPlan: 'PRO',
      from: location
    });
  };
  return <Button variant="transparent" intent="neutral" size={size} className={cn('cursor-pointer', className)} leftSlot={<HeaderPlanIcon planType={user.planType} accentClassName={accentClassName} />} onClick={handleClick} data-sentry-element="Button" data-sentry-component="PaidPlanHeaderButton" data-sentry-source-file="HeaderPlanStatusButton.tsx">
      <HeaderPlanStatusText planType={user.planType} endDate={user.planDuration?.endDate ?? ''} mono={Boolean(accentClassName)} data-sentry-element="HeaderPlanStatusText" data-sentry-source-file="HeaderPlanStatusButton.tsx" />
    </Button>;
};
const HeaderPlanIcon = ({
  planType,
  accentClassName
}: {
  planType: PlanType;
  accentClassName?: string;
}) => {
  if (planType === 'PRO') {
    return <BoltSmallRectangleColorVioletIcon size="xs" className={accentClassName} />;
  }
  if (planType === 'BASIC') {
    return <BoltSmallRectangleColorMintIcon size="xs" className={accentClassName} />;
  }
  return <span className="flex size-[12px] shrink-0 items-center justify-center rounded-[2px] bg-gray-300" data-sentry-component="HeaderPlanIcon" data-sentry-source-file="HeaderPlanStatusButton.tsx">
      <BoltFillIcon width={9} height={9} fill={ICON_COLOR.inverse} aria-hidden="true" data-sentry-element="BoltFillIcon" data-sentry-source-file="HeaderPlanStatusButton.tsx" />
    </span>;
};
const HeaderPlanStatusText = ({
  planType,
  endDate,
  mono = false
}: {
  planType: PlanType;
  endDate: string;
  /** true 면 남은 일수도 버튼 글자색 그대로(포인트색 없음). */
  mono?: boolean;
}) => {
  const {
    t
  } = useTranslation(['common']);
  if (planType === 'FREE') {
    return <span>{t('common:plan.usingFree')}</span>;
  }
  const daysRemaining = calculateDaysUntilExpiry(endDate);
  const daysColorClass = mono ? undefined : planType === 'BASIC' ? 'text-semi-strong' : 'text-strong';
  if (daysRemaining < 0) {
    return <span>{t('common:plan.usingPlan', {
        planType
      })}</span>;
  }
  return <span data-sentry-component="HeaderPlanStatusText" data-sentry-source-file="HeaderPlanStatusButton.tsx">
      <Trans t={t} i18nKey="common:plan.daysLeft" values={{
      planType,
      days: daysRemaining
    }} components={{
      Days: <span className={daysColorClass} />
    }} data-sentry-element="Trans" data-sentry-source-file="HeaderPlanStatusButton.tsx" />
    </span>;
};